import React, { useState } from 'react';

import {
	FormControl,
	FormLabel,
	FormGroup,
	FormControlLabel,
	Checkbox,
	TextField,
	Box,
	FormHelperText,
	Tooltip,
} from '@mui/material';

import {
	IQuestionnaireFormWidgetProps,
	IQuestionOption,
	IValueSelect,
} from 'src/features/questionnaire/domain/types';

const CheckBoxWithTextFieldWidget: React.FC<IQuestionnaireFormWidgetProps> = ({
	question,
	onValueChange,
	isDisabled,
	error,
	helperText = 'This field is invalid',
	compactMode = false,
}) => {
	const submittedValues = question.submitted_data?.value_multi_select ?? [];

	const [selectedCheckboxes, setSelectedCheckboxes] = useState<Set<string>>(
		new Set(submittedValues.map((v: IValueSelect) => v.value.toString())),
	);

	const [textInputs, setTextInputs] = useState<{ [key: string]: string }>(
		() => {
			const initial: { [key: string]: string } = {};
			for (const entry of submittedValues) {
				if (entry.text) {
					initial[entry.value.toString()] = entry.text;
				}
			}
			return initial;
		},
	);

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const optionId = e.target.value;
		const newSelected = new Set(selectedCheckboxes);

		if (e.target.checked) {
			newSelected.add(optionId);
		} else {
			newSelected.delete(optionId);
			delete textInputs[optionId];
		}

		setSelectedCheckboxes(newSelected);

		const updatedData = Array.from(newSelected).map((id) => ({
			value: id,
			text: textInputs[id] || '',
		}));

		onValueChange(question, updatedData, 'value_multi_select');
	};

	const handleTextFieldChange = (optionId: string, newText: string) => {
		const updatedInputs = {
			...textInputs,
			[optionId]: newText,
		};
		setTextInputs(updatedInputs);

		const updatedData = Array.from(selectedCheckboxes).map((id) => ({
			value: id,
			text: updatedInputs[id] || '',
		}));

		onValueChange(question, updatedData, 'value_multi_select');
	};

	const getToolTipTitleForTextField = (
		option: IQuestionOption,
		isChecked: boolean,
	) => {
		return (
			<span style={{ whiteSpace: 'pre-line' }}>
				Label: {option.label}
				{isChecked && textInputs[option.id]
					? `\n\nAns: ${textInputs[option.id]}`
					: ''}
			</span>
		);
	};

	return (
		<FormControl
			component='fieldset'
			required={question.is_required}
			disabled={isDisabled}
			sx={{ width: '100%', textAlign: 'left' }}
			error={error}
		>
			<FormLabel component='legend'>{question.text}</FormLabel>
			<FormGroup>
				{question.options.map((option) => {
					const optionId = option.id.toString();
					const isChecked = selectedCheckboxes.has(optionId);

					return (
						<Box
							key={option.id}
							sx={{
								display: 'flex',
								flexDirection: compactMode
									? 'column'
									: { xs: 'column', sm: 'row' },
								alignItems: compactMode
									? 'flex-start'
									: { xs: 'flex-start', sm: 'center' },
								mt: 0.5,
								width: '100%',
								gap: compactMode ? 1 : { xs: 1, sm: 0 },
							}}
						>
							<FormControlLabel
								control={
									<Checkbox
										checked={isChecked}
										onChange={handleCheckboxChange}
										value={optionId}
										disabled={isDisabled}
									/>
								}
								label={option.value}
								sx={{ marginLeft: 0 }}
							/>
							{option.allow_text && (
								<Tooltip
									title={getToolTipTitleForTextField(
										option,
										isChecked,
									)}
									arrow
									placement='left-start'
								>
									<TextField
										sx={{
											ml: 1.5,
											width: '100%',
											'& input': {
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
											},
										}}
										placeholder={option.label || ''}
										value={
											isChecked
												? textInputs[optionId] || ''
												: ''
										}
										disabled={!isChecked || isDisabled}
										onChange={(e) =>
											handleTextFieldChange(
												optionId,
												e.target.value,
											)
										}
										error={
											isChecked &&
											error &&
											!textInputs[optionId]
										}
									/>
								</Tooltip>
							)}
						</Box>
					);
				})}
			</FormGroup>
			{error && <FormHelperText>{helperText}</FormHelperText>}
		</FormControl>
	);
};

export default CheckBoxWithTextFieldWidget;
